import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async"; // added
import misson from "../images/mission.png";
import AboutUs2 from "../components/AboutHero";

const faqs = [
  {
    q: "What industries do you serve?",
    a: "We specialize in shipping, logistics, and allied industries, but our AI-driven solutions are adaptable to multiple domains.",
  },
  {
    q: "Are your solutions customizable?",
    a: "Yes, every solution is tailored to meet the unique requirements of your business and operations.",
  },
  {
    q: "Do you provide ongoing support?",
    a: "Absolutely. We provide 24/7 support and proactive system monitoring for all our clients.",
  },
  {
    q: "How do you ensure data security?",
    a: "We follow strict data security protocols, encryption standards, and compliance with global regulations.",
  },
];

const coreValues = [
  {
    id: 1,
    title: "AI Innovation",
    desc: "Harnessing the power of artificial intelligence to deliver smarter, future-ready solutions.",
    icon: "🤖",
  },
  {
    id: 2,
    title: "Customer Success",
    desc: "Putting clients at the center of everything we do and enabling their growth through technology.",
    icon: "🌍",
  },
  {
    id: 3,
    title: "Integrity",
    desc: "Building trust through transparency, accountability, and ethical practices.",
    icon: "🤝",
  },
  {
    id: 4,
    title: "Excellence",
    desc: "Striving for the highest quality in every solution, service, and interaction.",
    icon: "🏆",
  },
  {
    id: 5,
    title: "Collaboration",
    desc: "Working as partners with our clients, employees, and stakeholders to achieve shared success.",
    icon: "👥",
  },
  {
    id: 6,
    title: "Data-Driven Decisions",
    desc: "Leveraging data and analytics to create intelligent, result-oriented solutions.",
    icon: "📊",
  },
  // {
  //   id: 7,
  //   title: "Adaptability",
  //   desc: "Responding proactively to evolving industry needs with flexible and scalable innovations.",
  //   icon: "⚡",
  // },
];

const AboutUs = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About Us | Quadrangle Solutions</title>
        <meta
          name="description"
          content="Learn about Quadrangle Solutions: our mission, vision, core values, and commitment to AI-driven innovations for shipping and logistics."
        />
        <meta
          name="keywords"
          content="Quadrangle Solutions, About Us, AI solutions, logistics software, software development, core values"
        />
        <meta name="author" content="Quadrangle Solutions" />
        <meta property="og:title" content="About Us | Quadrangle Solutions" />
        <meta
          property="og:description"
          content="Discover how Quadrangle Solutions empowers businesses with AI-powered software solutions for logistics, shipping, and beyond."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://quadranglesolutions.com/about"
        />
        <meta property="og:image" content="%PUBLIC_URL%/QS_Logo.png" />
      </Helmet>

      <AboutUs2 />

      <div className="bg-gradient-to-br from-white via-green-50 to-emerald-100 text-gray-800 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 space-y-32">
          {/* Vision Section */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <motion.img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80"
              alt="Vision"
              className="rounded-2xl shadow-lg"
              whileHover={{ scale: 1.03 }}
            />
            <div>
              <h2 className="text-4xl font-bold text-green-700">Our Vision</h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                To be a globally trusted technology partner, empowering
                businesses in shipping, logistics, and beyond with AI-driven,
                scalable, and future-ready software solutions that drive
                efficiency, reliability, and growth.
              </p>
            </div>
          </section>

          {/* Mission Section */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-bold text-green-700">Our Mission</h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Our mission is to bridge the gap between industry challenges and
                technology by delivering AI-powered IT solutions that simplify
                complex operations, enhance productivity, and create long-term
                value for our clients. Guided by innovation, integrity, and
                customer-centricity, we strive to transform logistics and allied
                industries into smarter, more connected ecosystems.
              </p>
            </div>
            <motion.img
              src={misson}
              alt="Mission"
              className="rounded-2xl shadow-lg order-1 md:order-2"
              whileHover={{ scale: 1.03 }}
            />
          </section>

          {/* Core Values Section */}
          <section>
            <h2 className="text-4xl font-bold text-center text-green-700">
              Core Values
            </h2>
            <div className="grid md:grid-cols-3 gap-10 mt-12">
              {coreValues.map((value) => (
                <motion.div
                  key={value.id}
                  whileHover={{ y: -6 }}
                  className="p-8 bg-white rounded-xl shadow-lg border border-green-200 hover:border-green-400 transition"
                >
                  <div className="text-4xl mb-4">{value.icon}</div>
                  <h3 className="text-2xl font-semibold text-green-700">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 mt-2">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section>
            <h2 className="text-4xl font-bold text-center text-green-700">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto mt-12 space-y-4">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="border border-green-200 rounded-lg bg-white shadow-md"
                >
                  <button
                    className="w-full text-left px-6 py-4 flex justify-between items-center"
                    onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                  >
                    <span className="text-lg font-medium text-gray-800">
                      {faq.q}
                    </span>
                    <span className="text-green-600 text-xl">
                      {openFAQ === idx ? "−" : "+"}
                    </span>
                  </button>
                  <AnimatePresence>
                    {openFAQ === idx && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 pb-4 text-gray-600"
                      >
                        {faq.a}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-24 px-6">
            <div className="absolute inset-0 bg-gradient-to-r from-green-600 via-emerald-500 to-green-700"></div>
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="relative max-w-5xl mx-auto text-center text-white">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="p-12 rounded-3xl bg-white/10 backdrop-blur-xl shadow-2xl"
              >
                <h2 className="text-5xl font-extrabold drop-shadow-lg">
                  Ready to Transform Your Business?
                </h2>
                <p className="mt-6 text-lg max-w-2xl mx-auto text-green-100">
                  Let’s work together to build AI-powered solutions that
                  streamline your logistics, accelerate growth, and keep you
                  ahead of the curve.
                </p>

                <div className="mt-10 flex flex-wrap justify-center gap-6">
                  <motion.a
                    href="/contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 rounded-xl bg-white text-green-700 font-semibold shadow-lg hover:bg-green-50 transition"
                  >
                    Contact Us
                  </motion.a>
                  <motion.a
                    href="/services&product"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 rounded-xl border-2 border-white text-white font-semibold shadow-lg hover:bg-white/10 transition"
                  >
                    Explore Services
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </section>
        </div>

        {/* Decorative background shapes */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-green-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-emerald-200/30 rounded-full blur-3xl"></div>
      </div>
    </>
  );
};

export default AboutUs;
